import { memo } from "react";
import {
  Box,
  Stack,
  Typography,
  Chip,
  IconButton,
  Avatar,
  AvatarGroup,
  Tooltip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { colors, shadows } from "./Pipeline.styles";
import PipelineCardMenu from "./PipelineCardMenu";

const PipelineCard = memo(
  ({
    lead,
    columnSettings = null,
    onViewDetails,
    onContactClick,
    onMenuAction,
    isDragging = false,
    isPlaceholder = false,
    bulkSelectMode = false,
    isSelected = false,
    onSelectToggle,
    isFirstColumn = false,
    isLastColumn = false,
  }) => {
    const theme = useTheme();

    // Use normalized lead data
    const services = Array.isArray(lead.services) ? lead.services : [];
    const source = lead.source || "";
    const priority = lead.priority || "LOW";
    const telecaller = lead.telecaller;
    const telecallerTeam = lead.telecallerTeam;
    const createdAt = lead.createdAt ? new Date(lead.createdAt) : null;
    const city = lead.location || "";
    const hasNotes = false;

    const formatDate = (date) => {
      if (!date) return "";
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const isToday = date.toDateString() === today.toDateString();
      const isTomorrow = date.toDateString() === tomorrow.toDateString();

      if (isToday) return "TODAY";
      if (isTomorrow) return "TOMORROW";

      const month = date.toLocaleDateString("en-US", { month: "short" });
      const day = date.getDate();
      return `${month} ${day}`;
    };

    const getPriorityColor = () => {
      const p = priority?.toUpperCase();
      if (p === "HIGH") return "#DC2626";
      if (p === "MEDIUM") return "#F59E0B";
      return "#10B981";
    };

    // Generate consistent color based on service name using a simple hash function
    const getServiceColor = (serviceName) => {
      const serviceColors = [
        "#6366F1", // Indigo
        "#8B5CF6", // Purple
        "#EC4899", // Pink
        "#F59E0B", // Amber
        "#10B981", // Emerald
        "#06B6D4", // Cyan
        "#3B82F6", // Blue
        "#EF4444", // Red
        "#14B8A6", // Teal
        "#F97316", // Orange
      ];

      // Create a simple hash from the service name
      let hash = 0;
      const normalizedName = serviceName.toLowerCase().trim();
      for (let i = 0; i < normalizedName.length; i++) {
        hash = normalizedName.charCodeAt(i) + ((hash << 5) - hash);
      }

      // Get a positive index
      const index = Math.abs(hash) % serviceColors.length;
      return serviceColors[index];
    };

    const formatSource = (source) => {
      if (!source) return "Other";
      // Replace underscores with spaces and capitalize each word
      return source
        .replace(/_/g, " ")
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    const getSourceColor = () => {
      // Normalize source: replace underscores with spaces and lowercase for matching
      const sourceNormalized = source.toLowerCase().replace(/_/g, " ");
      // Better colors for different sources
      const sourceColors = {
        website: { bg: "#EEF2FF", color: "#3B82F6" },
        referral: { bg: "#ECFDF5", color: "#10B981" },
        google: { bg: "#FEF2F2", color: "#DC2626" },
        "google ads": { bg: "#FEF2F2", color: "#DC2626" },
        facebook: { bg: "#E7F5FF", color: "#0284C7" },
        "facebook ads": { bg: "#E7F5FF", color: "#0284C7" },
        instagram: { bg: "#FDF2F8", color: "#DB2777" },
        linkedin: { bg: "#EFF6FF", color: "#0284C7" },
        whatsapp: { bg: "#F0FDF4", color: "#16A34A" },
        advertisement: { bg: "#FEF3C7", color: "#D97706" },
        "walk-in": { bg: "#F5F5F5", color: "#6B7280" },
      };
      return sourceColors[sourceNormalized] || sourceColors["website"];
    };

    const priorityColor = getPriorityColor();
    const sourceColorObj = getSourceColor();

    // Get settings with defaults
    const showSourceBadge = columnSettings?.sourceBadge !== false;
    const showServiceIcon = columnSettings?.serviceIcon !== false;
    const showConfidenceMeter = columnSettings?.confidenceMeter !== false;
    const showFollowUpDate = columnSettings?.followUpDate !== false;
    const showQuickActions = columnSettings?.quickActions !== false;
    const accentColor = columnSettings?.accentColor || colors.primary;

    // Ghost card placeholder
    if (isPlaceholder) {
      return (
        <Box
          sx={{
            bgcolor: "rgba(99, 102, 241, 0.08)",
            borderRadius: "10px",
            px: 1.5,
            py: 1.25,
            mx: 1.25,
            mb: 0.75,
            border: `2px dashed ${colors.primary}`,
            boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)",
            transition: "all 0.2s ease",
            animation: "ghostPulse 1s ease-in-out infinite",
            "@keyframes ghostPulse": {
              "0%, 100%": { opacity: 0.7 },
              "50%": { opacity: 1 },
            },
          }}
        >
          <Stack spacing={1}>
            <Box
              sx={{
                height: 16,
                width: "60%",
                bgcolor: "rgba(99, 102, 241, 0.2)",
                borderRadius: "4px",
              }}
            />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  bgcolor: "rgba(99, 102, 241, 0.2)",
                }}
              />
              <Box
                sx={{
                  height: 10,
                  width: "20%",
                  bgcolor: "rgba(99, 102, 241, 0.15)",
                  borderRadius: "4px",
                }}
              />
            </Stack>
          </Stack>
        </Box>
      );
    }

    const handleCardClick = (e) => {
      if (bulkSelectMode) {
        e.stopPropagation();
        onSelectToggle?.(lead);
      } else {
        onViewDetails?.(lead);
      }
    };

    return (
      <Box
        onClick={handleCardClick}
        sx={{
          bgcolor: colors.bgCard,
          borderRadius: "12px",
          px: 2,
          py: 1.75,
          mx: 1.5,
          mb: 1.25,
          border: isSelected
            ? `3px solid ${theme.palette.primary.main}`
            : `2px solid ${colors.border}`,
          borderLeft: isSelected
            ? `3px solid ${theme.palette.primary.main}`
            : `3px solid ${theme.palette.primary.main}`,
          boxShadow: isDragging
            ? "0 10px 25px rgba(0,0,0,0.15)"
            : isSelected
            ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
            : "0 2px 6px rgba(0,0,0,0.06)",
          cursor: bulkSelectMode ? "pointer" : "grab",
          transition: "all 0.15s ease",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Header: Selection Icon + Name + Source Badge */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={1.25}
        >
          {/* Circular Selection Icon - shown in bulk select mode */}
          {bulkSelectMode && (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                onSelectToggle?.(lead);
              }}
              sx={{
                mr: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              {isSelected ? (
                <CheckCircleIcon
                  sx={{
                    fontSize: 22,
                    color: theme.palette.primary.main,
                  }}
                />
              ) : (
                <RadioButtonUncheckedIcon
                  sx={{
                    fontSize: 22,
                    color: colors.textMuted,
                    "&:hover": {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
              )}
            </Box>
          )}

          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: colors.textPrimary,
              lineHeight: 1.4,
              flex: 1,
              pr: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {lead.name || "Unknown"}
          </Typography>

          {showSourceBadge && (
            <Chip
              label={formatSource(source)}
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 600,
                bgcolor: sourceColorObj.bg,
                color: sourceColorObj.color,
                flexShrink: 0,
                "& .MuiChip-label": {
                  px: 0.75,
                },
              }}
            />
          )}

          {/* Card Menu - hidden in bulk select mode */}
          {!bulkSelectMode && (
            <Box sx={{ ml: 0.5 }}>
              <PipelineCardMenu
                lead={lead}
                onAction={onMenuAction}
                isFirstColumn={isFirstColumn}
                isLastColumn={isLastColumn}
              />
            </Box>
          )}
        </Stack>

        {/* Services (Left) + City (Right) in same row */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          {/* Services as Avatars */}
          {showServiceIcon && services.length > 0 && (
            <AvatarGroup
              max={3}
              sx={{
                "& .MuiAvatar-root": {
                  width: 24,
                  height: 24,
                  fontSize: 11,
                  fontWeight: 600,
                },
              }}
            >
              {services.slice(0, 3).map((service, index) => (
                <Tooltip key={index} title={service} arrow placement="top">
                  <Avatar sx={{ bgcolor: getServiceColor(service) }}>
                    {service.charAt(0).toUpperCase()}
                  </Avatar>
                </Tooltip>
              ))}
              {services.length > 3 && (
                <Tooltip title={`${services.length - 3} more`} arrow>
                  <Avatar sx={{ bgcolor: "#94A3B8" }}>
                    +{services.length - 3}
                  </Avatar>
                </Tooltip>
              )}
            </AvatarGroup>
          )}

          {/* City Location */}
          {city && (
            <Stack direction="row" alignItems="center" spacing={0.25}>
              <LocationOnOutlinedIcon
                sx={{ fontSize: 12, color: colors.textMuted }}
              />
              <Typography
                sx={{
                  fontSize: 10,
                  color: colors.textMuted,
                  whiteSpace: "nowrap",
                }}
              >
                {city}
              </Typography>
            </Stack>
          )}
        </Stack>

        {/* Assignee Row */}
        {telecaller && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              mb: 1,
              p: 0.75,
              bgcolor: "#F8FAFC",
              borderRadius: "8px",
            }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                bgcolor: "#E2E8F0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: 600,
                color: colors.textSecondary,
              }}
            >
              {telecaller?.charAt(0).toUpperCase() || "?"}
            </Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 500,
                color: colors.textSecondary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {telecaller}
            </Typography>
            {telecallerTeam && (
              <Typography
                sx={{
                  fontSize: 9,
                  color: colors.textMuted,
                  whiteSpace: "nowrap",
                  bgcolor: "#E2E8F0",
                  px: 0.5,
                  py: 0.25,
                  borderRadius: "3px",
                }}
              >
                {telecallerTeam}
              </Typography>
            )}
          </Stack>
        )}

        {/* Footer: Contact Icons + Date */}
        {(showQuickActions || showFollowUpDate) && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              borderTop: `1px solid ${colors.borderLight}`,
              pt: 1,
              mt: 0.5,
            }}
          >
            {showQuickActions && (
              <Stack direction="row" spacing={0}>
                <Tooltip title="Call" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onContactClick?.({
                        type: "call",
                        phone: lead.phone,
                        email: lead.email,
                        address: city,
                        clientName: lead.name,
                      });
                    }}
                    sx={{
                      width: 22,
                      height: 22,
                      bgcolor: "transparent",
                      "&:hover": { bgcolor: colors.borderLight },
                    }}
                  >
                    <PhoneOutlinedIcon
                      sx={{ fontSize: 12, color: colors.textMuted }}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="WhatsApp" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onContactClick?.({
                        type: "whatsapp",
                        phone: lead.phone,
                        email: lead.email,
                        address: city,
                        clientName: lead.name,
                      });
                    }}
                    sx={{
                      width: 22,
                      height: 22,
                      bgcolor: "transparent",
                      "&:hover": { bgcolor: colors.borderLight },
                    }}
                  >
                    <ChatBubbleOutlineIcon
                      sx={{ fontSize: 12, color: colors.textMuted }}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Details" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onContactClick?.({
                        type: "details",
                        phone: lead.phone,
                        email: lead.email,
                        address: city,
                        clientName: lead.name,
                      });
                    }}
                    sx={{
                      width: 22,
                      height: 22,
                      bgcolor: "transparent",
                      "&:hover": { bgcolor: colors.borderLight },
                    }}
                  >
                    <DescriptionOutlinedIcon
                      sx={{ fontSize: 12, color: colors.textMuted }}
                    />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}

            {showFollowUpDate && (
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  color:
                    formatDate(createdAt) === "TODAY"
                      ? "#DC2626"
                      : colors.textMuted,
                }}
              >
                {formatDate(createdAt)}
              </Typography>
            )}
          </Stack>
        )}
      </Box>
    );
  },
);

PipelineCard.displayName = "PipelineCard";
export default PipelineCard;
